import type React from "react";
import { Box, Typography } from "@mui/material";
import type { Proposal } from "../types/proposal.types";
import type { CoverPageTemplate } from "../types/cover.types";

interface CoverPageProps {
  proposal: Proposal;
  template?: CoverPageTemplate | null;
}

const CoverPage: React.FC<CoverPageProps> = ({ proposal, template }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatDateShort = (dateString: string) => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return date.toLocaleDateString("en-US", options);
  };

  const formatDateShort2 = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const backgroundImage =
    template?.background_image || "/placeholder.svg?height=1056&width=816";

  const hasPhoto =
    !proposal.eventTitle && proposal.booking?.deceased?.[0]?.person_image;

  // Layout 1 - Original
  const renderLayout1 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        maxWidth: "800px",
        gap: 4,
      }}
    >
      <Box sx={{ mb: 4 }}>
        {proposal.eventTitle ? (
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: "4.5rem",
              color: template?.theme_color || "#6B4423",
              letterSpacing: "0.05em",
              lineHeight: 1.1,
            }}
          >
            EVENT
          </Typography>
        ) : (
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: "4.5rem",
              color: template?.theme_color || "#6B4423",
              letterSpacing: "0.05em",
              lineHeight: 1.1,
            }}
          >
            SERVICE
          </Typography>
        )}

        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: "4.5rem",
            color: template?.theme_color || "#6B4423",
            letterSpacing: "0.05em",
            lineHeight: 1.1,
          }}
        >
          PROPOSAL
        </Typography>
      </Box>
      {proposal.eventTitle ? (
        <Box sx={{ my: 6 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "2rem",
              color: template?.theme_color || "#6B4423",
              letterSpacing: "0.05em",
              lineHeight: 1.1,
            }}
          >
            {proposal.eventTitle}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ my: 6 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.25rem",
              color: template?.theme_color || "#6B4423",
              fontWeight: 500,
              mb: 2,
            }}
          >
            In Loving Memory Of
          </Typography>
          <Typography
            sx={{
              fontSize: "3.5rem",
              color: template?.theme_color || "#6B4423",
              fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
              fontStyle: "italic",
              fontWeight: 400,
            }}
          >
            {proposal.deceasedName}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 8 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
            mb: 1,
          }}
        >
          Client: {proposal.client.name}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
          }}
        >
          Date: {formatDate(proposal.booking?.services?.[0]?.date_of_service || "")}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.1rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
          }}
        >
          Venue:{" "}
          {`${proposal.booking?.services?.[0]?.venue.name || ""}${
            proposal.booking?.services?.[0]?.venue.name &&
            proposal.booking?.services?.[0]?.venue.address_one
              ? ", "
              : ""
          }${proposal.booking?.services?.[0]?.venue.address_one || ""}`}
        </Typography>
      </Box>
    </Box>
  );

  // Layout 2 - Jamie
  const renderLayout2 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        maxWidth: "800px",
        gap: 2,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 700,
            fontSize: "5rem",
            color: "#ffffff",
            lineHeight: 1.2,
          }}
        >
          {proposal.eventTitle ? "Event Proposal" : "Service Proposal"}
        </Typography>
      </Box>

      {proposal.eventTitle ? (
        <Box sx={{ my: 2 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1.6rem",
              color: "#ffffff",
              letterSpacing: "0.05em",
            }}
          >
            {proposal.eventTitle}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ my: 2.5 }}>
          <Typography
            sx={{
              fontSize: "1.6rem",
              color: "#ffffff",
              fontWeight: 500,
              mb: 0.5,
              letterSpacing: "0.1em",
            }}
          >
            in Loving Memory Of
          </Typography>
          <Typography
            sx={{
              fontSize: "2.6rem",
              color: "#ffffff",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            {proposal.deceasedName}
          </Typography>
        </Box>
      )}

      {!proposal.eventTitle &&
        proposal.deceasedBirthDate &&
        proposal.deceasedPassedDate && (
          <Typography
            sx={{
              fontSize: "1.6rem",
              color: "#ffffff",
              fontWeight: 500,
              mb: 0.2,
            }}
          >
            {formatDateShort(proposal.deceasedBirthDate)} –{" "}
            {formatDateShort(proposal.deceasedPassedDate)}
          </Typography>
        )}

      {!proposal.eventTitle &&
        proposal.booking?.deceased?.[0]?.person_image && (
          <Box
            sx={{
              width: "360px",
              height: "260px",
              mb: 2.5,
              border: "12px solid #ededed",
              overflow: "hidden",
            }}
          >
            <img
              src={proposal.booking?.deceased?.[0]?.person_image}
              alt={proposal.deceasedName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        )}

      <Box sx={{ mt: 6 }}>
        <Typography
          sx={{
            fontSize: "1.3rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Client Name: {proposal.client.name}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.3rem",
            color: template?.theme_color || "#6B4423",
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Service Date:{" "}
          {formatDate(proposal.booking?.services?.[0]?.date_of_service || "")}
        </Typography>
        {proposal.booking?.services?.[0]?.venue?.name && (
          <Typography
            sx={{
              fontSize: "1.3rem",
              color: template?.theme_color || "#6B4423",
              fontWeight: 600,
            }}
          >
            Service Location: {proposal.booking.services[0].venue.name}
            {proposal.booking.services[0].venue.address_one
              ? `, ${proposal.booking.services[0].venue.address_one}`
              : ""}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // Layout 3
  const renderLayout3 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        width: "100%",
        maxWidth: "700px",
        gap: 0,
        //pt: 4,
        mt: -7,
      }}
    >
      {/* Arched Photo Frame */}
      {proposal.booking?.deceased?.[0]?.person_image && (
        <Box
          sx={{
            width: "315px",
            height: "420px",
            position: "relative",
            mb: 8,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "170px 170px 0 0",
              overflow: "hidden",
              border: "2px solid #B88A6A",
              backgroundColor: "#f5f5f0",
            }}
          >
            <img
              src={proposal.booking.deceased[0].person_image}
              alt={proposal.deceasedName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Service Proposal Banner */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "600px",
          backgroundColor: "#2D4A4A",
          py: 0.8,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: "1.625rem",
            color: "#FFFFFF",
            fontFamily: "'Libre Baskerville', serif",
            //fontWeight: 100,
            textTransform: "uppercase",
          }}
        >
          Service Proposal For
        </Typography>
      </Box>

      {/* Deceased Name */}
      <Typography
        sx={{
          fontSize: "4.5rem",
          color: "#2D4A4A",
          fontFamily: "'Playfair Display', serif",
          fontWeight: 400,
          lineHeight: 1.1,
          mb: 2,
        }}
      >
        {proposal.deceasedName}
      </Typography>

      {/* Life Dates */}
      {proposal.deceasedBirthDate && proposal.deceasedPassedDate && (
        <Typography
          sx={{
            fontSize: "1.3rem",
            color: "#2D4A4A",
            fontWeight: 300,
            mb: 4,
            letterSpacing: "0.05em",
          }}
        >
          {formatDateShort2(proposal.deceasedBirthDate)
            .replace(/,/g, "")
            .replace(/ /g, ".")}{" "}
          -{" "}
          {formatDateShort2(proposal.deceasedPassedDate)
            .replace(/,/g, "")
            .replace(/ /g, ".")}
        </Typography>
      )}

      {/* Divider Line */}
      <Box
        sx={{
          width: "80%",
          borderTop: "1px solid #2D4A4A",
          mb: 3,
        }}
      />

      {/* Client Information */}
      <Box sx={{ width: "80%", textAlign: "center" }}>
        <Typography
          sx={{
            fontSize: "1.1rem",
            color: "#2D4A4A",
            fontWeight: 300,
            mb: 1,
            letterSpacing: "0.05em",
          }}
        >
          CLIENT NAME: {proposal.client.name.toUpperCase()}
        </Typography>
        <Typography
          sx={{
            fontSize: "1.1rem",
            color: "#2D4A4A",
            fontWeight: 300,
            mb: 1,
            letterSpacing: "0.05em",
          }}
        >
          SERVICE DATE:{" "}
          {proposal.booking?.services?.[0]?.date_of_service
            ? formatDateShort2(proposal.booking.services[0].date_of_service)
                .replace(/,/g, "")
                .replace(/ /g, ".")
            : ""} 
        </Typography>
        {proposal.booking?.services?.[0]?.venue && (
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#2D4A4A",
              fontWeight: 300,
              letterSpacing: "0.05em",
            }}
          >
            SERVICE LOCATION:{" "}
            {`${proposal.booking?.services?.[0]?.venue.name || ""}${
              proposal.booking?.services?.[0]?.venue.name &&
              proposal.booking?.services?.[0]?.venue.address_one
                ? ", "
                : ""
            }${proposal.booking?.services?.[0]?.venue.address_one || ""}`}
          </Typography>
        )}
      </Box>

      {/* Bottom Divider Line */}
      <Box
        sx={{
          width: "80%",
          borderTop: "1px solid #2D4A4A",
          mt: 3,
        }}
      />
    </Box>
  );

  // Layout 4 - Vintage Cover
  const renderLayout4 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        position: "relative",
        px: 6,
        py: 4,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          mb: 6,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "1.5rem",
              color: "#3D2817",
              fontWeight: 900,
              letterSpacing: "0.05em",
              lineHeight: 1.2,
            }}
          >
            SERVICE
          </Typography>
          <Typography
            sx={{
              fontSize: "1.5rem",
              color: "#3D2817",
              fontWeight: 900,
              letterSpacing: "0.05em",
              lineHeight: 1.2,
            }}
          >
            PROPOSAL
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          flex: 1,
          justifyContent: "center",
          mt: -4,
        }}
      >
        {/* Deceased Surname */}
        <Typography
          sx={{
            fontSize: "4rem",
            color: "#3D2817",
            fontFamily: "'Playfair Display', serif",
            fontWeight: 600,
            letterSpacing: "0.15em",
            lineHeight: 1,
            mb: 1,
            textTransform: "uppercase",
          }}
        >
          {proposal.deceasedName.split(" ").pop()}
        </Typography>

        {/* Deceased First Name */}
        <Typography
          sx={{
            fontSize: "4rem",
            color: "#3D2817",
            fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
            fontStyle: "italic",
            fontWeight: 400,
            mb: 6,
          }}
        >
          {proposal.deceasedName.split(" ").slice(0, -1).join(" ")}
        </Typography>

        {/* Photo */}
        {proposal.booking?.deceased?.[0]?.person_image && (
          <Box
            sx={{
              width: "600px",
              height: "380px",
              overflow: "hidden",
              mb: 6,
              //border: "8px solid #FFFFFF",
              //boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <img
              src={proposal.booking.deceased[0].person_image}
              alt={proposal.deceasedName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                filter: "grayscale(100%)",
              }}
            />
          </Box>
        )}

        {/* Client Information */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#3D2817",
              fontWeight: 400,
              mb: 0.5,
            }}
          >
            Client Name: {proposal.client.name}
          </Typography>
          <Typography
            sx={{
              fontSize: "1.1rem",
              color: "#3D2817",
              fontWeight: 400,
              mb: 0.5,
            }}
          >
            Service Date:{" "}
            {proposal.booking?.services?.[0]?.date_of_service
              ? formatDateShort2(proposal.booking.services[0].date_of_service)
              : ""} 
          </Typography>
          {proposal.booking?.services?.[0]?.venue && (
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#3D2817",
                fontWeight: 400,
              }}
            >
              Service Location:{" "}
              {`${proposal.booking?.services?.[0]?.venue.name || ""}${
                proposal.booking?.services?.[0]?.venue.name &&
                proposal.booking?.services?.[0]?.venue.address_one
                  ? ", "
                  : ""
              }${proposal.booking?.services?.[0]?.venue.address_one || ""}`}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );

  // Layout 5 - Modern Blend
  const renderLayout5 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        width: "100%",
        height: "100%",
        pt: 12,
        px: 6,
      }}
    >
      {/* Service Proposal Header */}
      <Typography
        sx={{
          fontSize: "1.5rem",
          color: "#3D2817",
          fontWeight: 600,
          letterSpacing: "0.3em",
          mb: 4,
          textTransform: "uppercase",
        }}
      >
        Service Proposal
      </Typography>

      {/* Deceased Name */}
      <Typography
        sx={{
          fontSize: "4rem",
          color: "#3D2817",
          fontWeight: 900,
          letterSpacing: "0.05em",
          lineHeight: 0.9,
          mb: 1,
          textTransform: "uppercase",
        }}
      >
        {proposal.deceasedName.split(" ")[0]}
      </Typography>
      <Typography
        sx={{
          fontSize: "4rem",
          color: "#3D2817",
          fontWeight: 900,
          letterSpacing: "0.05em",
          lineHeight: 0.9,
          mb: 6,
          textTransform: "uppercase",
        }}
      >
        {proposal.deceasedName.split(" ").slice(1).join(" ")}
      </Typography>

      {/* Circular Photo */}
      {proposal.booking?.deceased?.[0]?.person_image && (
        <Box
          sx={{
            width: "280px",
            height: "280px",
            borderRadius: "50%",
            overflow: "hidden",
            mb: 4,
            border: "8px solid #FFFFFF",
            //boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          <img
            src={proposal.booking.deceased[0].person_image}
            alt={proposal.deceasedName}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
      )}

      {/* Life Dates */}
      {proposal.deceasedBirthDate && proposal.deceasedPassedDate && (
        <Typography
          sx={{
            fontSize: "1.1rem",
            color: "#3D2817",
            fontWeight: 400,
            mb: 4,
            letterSpacing: "0.05em",
          }}
        >
          {formatDateShort2(proposal.deceasedBirthDate)} -{" "}
          {formatDateShort2(proposal.deceasedPassedDate)}
        </Typography>
      )}

      {/* Divider Line */}
      <Box
        sx={{
          width: "100px",
          borderTop: "2px solid #3D2817",
          mb: 4,
        }}
      />

      {/* Client Information */}
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography
          sx={{
            fontSize: "1rem",
            color: "#3D2817",
            fontWeight: 400,
            mb: 1,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          Client Name: {proposal.client.name}
        </Typography>
        <Typography
          sx={{
            fontSize: "1rem",
            color: "#3D2817",
            fontWeight: 400,
            mb: 1,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          Service Date:{" "}
          {proposal.booking?.services?.[0]?.date_of_service
            ? formatDateShort2(proposal.booking.services[0].date_of_service)
            : ""} 
        </Typography>
        {proposal.booking?.services?.[0]?.venue && (
          <Typography
            sx={{
              fontSize: "1rem",
              color: "#3D2817",
              fontWeight: 400,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Service Location:{" "}
            {`${proposal.booking?.services?.[0]?.venue.name || ""}${
              proposal.booking?.services?.[0]?.venue.name &&
              proposal.booking?.services?.[0]?.venue.address_one
                ? ", "
                : ""
            }${proposal.booking?.services?.[0]?.venue.address_one || ""}`}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // Layout 6 - Conference Template
  const renderLayout6 = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        position: "relative",
        pt: 20,
      }}
    >
      {/* Service Proposal Header */}
      <Box sx={{ pl: 20, mb: 4 }}>
        <Typography
          sx={{
            fontSize: "5rem",
            color: "#4A5F6D",
            fontWeight: 900,
            letterSpacing: "0.05em",
            lineHeight: 1,
            mb: 1,
            textTransform: "uppercase",
            textAlign: "left",
          }}
        >
          Service
        </Typography>
        <Typography
          sx={{
            fontSize: "5rem",
            color: "#4A5F6D",
            fontWeight: 900,
            letterSpacing: "0.05em",
            lineHeight: 1,
            textTransform: "uppercase",
            textAlign: "left",
          }}
        >
          Proposal
        </Typography>
      </Box>

      {/* Centered */}
      <Box sx={{ textAlign: "left", mb: 8, pl: 20 }}>
        <Typography
          sx={{
            fontSize: "3rem",
            color: "#4A5F6D",
            fontWeight: 400,
            textTransform: "uppercase",
          }}
        >
          For
        </Typography>
      </Box>

      {/* Banner */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#4A5F6D",
          py: 1.5,
          px: 20,
          mb: 12,
        }}
      >
        <Typography
          sx={{
            fontSize: "3rem",
            color: "#FFFFFF",
            fontWeight: 800,
            textAlign: "left",
            textTransform: "uppercase",
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          {(proposal.eventTitle || proposal.deceasedName).split(" ")[0]}
        </Typography>

        <Typography
          sx={{
            fontSize: "3rem",
            color: "#FFFFFF",
            fontWeight: 800,
            textAlign: "left",
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {(proposal.eventTitle || proposal.deceasedName)
            .split(" ")
            .slice(1)
            .join(" ")}
        </Typography>
      </Box>

      {/* Date and Client Information */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Typography
          sx={{
            fontSize: "2rem",
            color: "#4A5F6D",
            fontWeight: 600,
          }}
        >
          Date:{" "}
          {proposal.booking?.events?.[0]?.start_date_of_event ||
          proposal.booking?.services?.[0]?.date_of_service
            ? formatDateShort2(
                proposal.booking?.events?.[0]?.start_date_of_event ||
                proposal.booking?.services?.[0]?.date_of_service || ""
              )
            : ""} 
        </Typography>
        <Typography
          sx={{
            fontSize: "2rem",
            color: "#4A5F6D",
            fontWeight: 600,
          }}
        >
          Client: {proposal.client.name}
        </Typography>
        {(proposal.booking?.events?.[0]?.venue?.name || proposal.booking?.services?.[0]?.venue?.name) && (
          <Typography
            sx={{
              fontSize: "2rem",
              color: "#4A5F6D",
              fontWeight: 600,
            }}
          >
            Venue:{" "}
            {proposal.booking?.events?.[0]?.venue?.name ||
              proposal.booking?.services?.[0]?.venue?.name || ""}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderContent = () => {
    if (template?.layout === 6) {
      return renderLayout6();
    }

    if (template?.layout === 5) {
      return renderLayout5();
    }

    if (template?.layout === 4) {
      return renderLayout4();
    }

    if (template?.layout === 3) {
      return renderLayout3();
    }

    if (template?.layout === 2) {
      if (hasPhoto) return renderLayout2();
      return renderLayout1();
    }

    if (template?.layout === 1) {
      return renderLayout1();
    }

    if (hasPhoto) return renderLayout2();
    return renderLayout1();
  };

  return (
    <Box
      id="cover-page"
      sx={{
        //width: "210mm",
        width: "100%",
        height: "297mm",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        //p: 6,
        pt:
          template?.layout === 3
            ? 24
            : hasPhoto && template?.layout === 2
              ? 24
              : 6,
        display: "flex",
        flexDirection: "column",
        justifyContent:
          template?.layout === 3
            ? "flex-start"
            : hasPhoto && template?.layout === 2
              ? "flex-start"
              : "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default CoverPage;
